// calendarHelpers.ts
// Pure functions for CalendarPicker component.
// No React, no styled-components — only logic.

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

const DAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

export interface CalendarDay {
    date: string; // YYYY-MM-DD, "" for padding
    dayOfMonth: number; // 1–31, 0 for padding
    dayName: string; // "Monday" etc, "" for padding
    isPadding: boolean;
}

export interface CalendarMonth {
    year: number;
    month: number; // 0-indexed
    monthName: string;
    weeks: CalendarDay[][];
}

/**
 * Return the days-of-week (e.g. ["Saturday","Sunday"]) that have
 * at least one timeslot for the given pool key.
 */
export function getAvailableDaysForPool(
    pool: string,
    timeslots: { [pool: string]: { [day: string]: string[] } }
): string[] {
    const poolSlots = timeslots[pool];
    if (!poolSlots) {
        return [];
    }
    return Object.keys(poolSlots);
}

/**
 * Return true if the given YYYY-MM-DD date is:
 * - tomorrow or later (strictly after todayStr)
 * - within today + maxDaysForward (default 60, ~2 months)
 * - on a day-of-week present in availableDays
 */
export function isDateAvailable(
    dateStr: string,
    availableDays: string[],
    todayStr: string,
    maxDaysForward: number = 60
): boolean {
    if (!dateStr || !todayStr) {
        return false;
    }
    // Block today and past dates (booking starts from tomorrow)
    if (dateStr <= todayStr) {
        return false;
    }
    // Beyond range check
    const todayDate = new Date(todayStr);
    const maxDate = new Date(todayDate);
    maxDate.setDate(maxDate.getDate() + maxDaysForward);
    const maxDateStr = maxDate.toISOString().split("T")[0];
    if (dateStr > maxDateStr) {
        return false;
    }
    // Day-of-week check
    // Use UTC to avoid timezone offset shifting the date
    const [year, month, day] = dateStr.split("-").map(Number);
    const dateObj = new Date(Date.UTC(year, month - 1, day));
    const dayName = DAY_NAMES[dateObj.getUTCDay()];
    return availableDays.includes(dayName);
}

/**
 * Build a month grid for the given year and 0-indexed month.
 * Returns CalendarMonth with 4–6 weeks of 7 CalendarDay cells.
 * First column is Sunday (index 0), last is Saturday (index 6).
 * Padding cells fill the beginning and end of the grid.
 */
export function buildMonthGrid(year: number, month: number): CalendarMonth {
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
    const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDayOfMonth.getUTCDate();
    // Padding at start: firstDayOfMonth.getUTCDay() = 0 (Sun) through 6 (Sat)
    const startPadding = firstDayOfMonth.getUTCDay();

    const days: CalendarDay[] = [];

    // Leading padding
    for (let i = 0; i < startPadding; i++) {
        days.push({ date: "", dayOfMonth: 0, dayName: "", isPadding: true });
    }

    // Actual month days
    for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(Date.UTC(year, month, d));
        const mm = String(month + 1).padStart(2, "0");
        const dd = String(d).padStart(2, "0");
        days.push({
            date: `${year}-${mm}-${dd}`,
            dayOfMonth: d,
            dayName: DAY_NAMES[dateObj.getUTCDay()],
            isPadding: false
        });
    }

    // Trailing padding to complete last row
    const trailingPadding = (7 - (days.length % 7)) % 7;
    for (let i = 0; i < trailingPadding; i++) {
        days.push({ date: "", dayOfMonth: 0, dayName: "", isPadding: true });
    }

    // Chunk into weeks of 7
    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    return {
        year,
        month,
        monthName: MONTH_NAMES[month],
        weeks
    };
}
